/*!

=========================================================
* Argon Dashboard React - v1.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from 'react';
import moment from 'moment';

// reactstrap components
import {
  Card,
  CardHeader,
  CardFooter,
  Pagination,
  PaginationItem,
  PaginationLink,
  Table,
  Container,
  Row,
  Col,
  Input,
  Button,
  Spinner,
} from 'reactstrap';
// core components
import Header from 'components/Headers/Header.js';
import { axiosGet } from 'util/apiConfig.js';
import { accountLabel } from 'util/constants';

class AllPayments extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      paymentData: [],
      loading: false,
      searchValue: '',
      page: 1,
      totalPage: 1,
      limit: 100,
      total: 0,
    };
  }

  componentDidMount() {
    this.getAllPayments();
  }

  getAllPayments = () => {
    const { page, limit } = this.state;
    this.setState({ loading: true });
    // Using the endpoint specified by the user: api/v1/payment/all-payments
    // apiConfig prepends the base path which includes api/v1/
    axiosGet(`payment/all-payments?page=${page}&limit=${limit}`)
      .then((response) => {
        if (!response.isError) {
          const result = Array.isArray(response.data) ? response.data : (response.data?.data || response.data?.result || []);
          this.setState({
            paymentData: result,
            total: response.data?.total || 0,
            loading: false,
          });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch((error) => {
        console.error("Error fetching payments:", error);
        this.setState({ loading: false });
      });
  };

  changePage = (status) => {
    const { page, total, limit } = this.state;
    if (status === 'next' && page * limit < total) {
      this.setState({ page: page + 1 }, () => this.getAllPayments());
    } else if (status === 'prev' && page > 1) {
      this.setState({ page: page - 1 }, () => this.getAllPayments());
    }
  };

  render() {
    const { paymentData, loading, searchValue } = this.state;
    const dataArray = Array.isArray(paymentData) ? paymentData : [];

    // Simple filter logic for search
    const filteredData = dataArray.filter(item => {
      if (!searchValue) return true;
      const search = searchValue.toLowerCase();
      return (
        (item.email && item.email.toLowerCase().includes(search)) ||
        (item.name && item.name.toLowerCase().includes(search)) ||
        (item.paymentId && item.paymentId.toLowerCase().includes(search))
      );
    });

    return (
      <>
        <Header />
        {/* Page content */}
        <Container className='mt--7' fluid>
          <Row>
            <div className='col'>
              <Card className='shadow'>
                <CardHeader className='border-0'>
                  <Row className="align-items-center">
                    <Col xs="8">
                      <h3 className='mb-0'>All Payments</h3>
                    </Col>
                    <Col xs="4" className="text-right">
                      <Input
                        placeholder="Search by name, email or ID"
                        type="text"
                        value={searchValue}
                        onChange={(e) => this.setState({ searchValue: e.target.value })}
                      />
                    </Col>
                  </Row>
                </CardHeader>
                <Table className='align-items-center table-flush' responsive>
                  <thead className='thead-light'>
                    <tr>
                      <th scope='col'>Name</th>
                      <th scope='col'>Email</th>
                      <th scope='col'>Category</th>
                      <th scope='col'>Amount</th>
                      <th scope='col'>Currency</th>
                      <th scope='col'>Status</th>
                      <th scope='col'>Date Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          <Spinner color="primary" />
                          <p className="mt-2">Loading payments...</p>
                        </td>
                      </tr>
                    ) : filteredData.length > 0 ? (
                      filteredData.map((data, index) => (
                        <tr key={index}>
                          <td>{data.name || 'N/A'}</td>
                          <td>{data.email || 'N/A'}</td>
                          <td>{accountLabel(data.category) || 'N/A'}</td>
                          <td>{data.amount || '0'}</td>
                          <td>{data.currency || 'USD'}</td>
                          <td>
                            <span className={`badge badge-dot mr-4`}>
                              <i className={data.status === 'succeeded' || data.status === 'paid' ? 'bg-success' : 'bg-warning'} />
                              <span className="status">{data.status || 'N/A'}</span>
                            </span>
                          </td>
                          <td>{data.dateTime ? moment(data.dateTime).format('DD-MM-YYYY HH:mm') : 'N/A'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          No payments found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <CardFooter className='py-4'>
                  <nav aria-label='...'>
                    <Pagination
                      className='pagination justify-content-end mb-0'
                      listClassName='justify-content-end mb-0'
                    >
                      <PaginationItem className={this.state.page <= 1 ? 'disabled' : ''}>
                        <PaginationLink
                          onClick={(e) => {
                            e.preventDefault();
                            this.changePage('prev');
                          }}
                          tabIndex='-1'
                        >
                          <i className='fas fa-angle-left' />
                          <span className='sr-only'>Previous</span>
                        </PaginationLink>
                      </PaginationItem>

                      <PaginationItem className='active'>
                        <PaginationLink onClick={(e) => e.preventDefault()}>
                          {this.state.page}
                        </PaginationLink>
                      </PaginationItem>

                      <PaginationItem
                        className={this.state.page * this.state.limit >= this.state.total ? 'disabled' : ''}
                      >
                        <PaginationLink
                          onClick={(e) => {
                            e.preventDefault();
                            this.changePage('next');
                          }}
                        >
                          <i className='fas fa-angle-right' />
                          <span className='sr-only'>Next</span>
                        </PaginationLink>
                      </PaginationItem>
                    </Pagination>
                  </nav>
                </CardFooter>
              </Card>
            </div>
          </Row>
        </Container>
      </>
    );
  }
}

export default AllPayments;
